/* eslint-disable react/no-array-index-key */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ErrorFeedback } from 'components/Form/styles';
import { Button, Form } from 'components';
import { FieldArray } from 'formik';
import { FaPlus, FaMinus } from 'react-icons/fa';
import styled from 'styled-components';
import { media } from 'utils/rwd';
import constructUrlPreview from 'utils/constructUrlPreview';
import { useUpdateCheckoutConfiguration } from 'api/regions/hooks';
import { childProduct } from '../../api/models/Product';
import validationSchema from './schemas/index';

const productKeys = Object.keys(childProduct).filter(
  (key) => ['id', 'uid'].includes(key) === false,
);

const mapFormValuesToCheckoutConfiguration = (values) => ({
  destination_url: values.destinationUrl,
  location_tracking_id_name: values.locationTrackingIdName,
  retailer_tracking_id_name: values.retailerTrackingIdName,
  brand_tracking_id_name: values.brandTrackingIdName,
  redirect_url_parameter_name: values.redirectUrlParameterName,
  redirect_url: values.redirectUrl,
  custom_parameters: values.customParameters.map((param) => ({
    key: param.key,
    value: param.value,
  })),
  product_parameters: values.productParameters.map((param) => ({
    product_field: param.productField,
    parameter_name: param.parameterName,
  })),
  product_encoding: values.productEncoding,
  quantity_parameter_name: values.quantityParameterName,
});

const initialFormValues = {
  destinationUrl: '',
  customParameters: [
    {
      key: '',
      value: '',
    },
  ],
  productParameters: [
    {
      productField: '',
      parameterName: '',
    },
  ],
};

const CheckoutConfigForm = ({ checkoutUid, initialValues = initialFormValues }) => {
  const { t } = useTranslation(['pages']);
  const [urlPreview, setUrlPreview] = useState(constructUrlPreview(initialValues));
  const { mutateAsync: updateCheckoutConfiguration } = useUpdateCheckoutConfiguration();

  const quantityOptions = useMemo(
    () => [
      {
        label: t('checkoutConfiguration.quantityIndexedLabel'),
        value: 'indexed',
        description: t('checkoutConfiguration.quantityIndexedDescription'),
        example: t('checkoutConfiguration.quantityIndexedExample'),
      },
      {
        label: t('checkoutConfiguration.quantityCommaLabel'),
        value: 'comma',
        description: t('checkoutConfiguration.quantityCommaDescription'),
        example: t('checkoutConfiguration.quantityCommaExample'),
      },
      {
        label: t('checkoutConfiguration.quantityJsonLabel'),
        value: 'json',
        description: t('checkoutConfiguration.quantityJsonDescription'),
        example: t('checkoutConfiguration.quantityJsonExample'),
      },
    ],
    [t],
  );

  const handleValueChange = (field, value, setValues) => {
    setValues((prevValues) => {
      const newValues = JSON.parse(JSON.stringify(prevValues));

      const fields = field.split('.');
      let current = newValues;
      for (let i = 0; i < fields.length - 1; i++) {
        current = current[fields[i]];
      }
      current[fields[fields.length - 1]] = value;

      const preview = constructUrlPreview(newValues);
      setUrlPreview(preview);

      return newValues;
    });
  };

  const handleSubmit = async (values) => {
    const configuration = mapFormValuesToCheckoutConfiguration(values);
    await updateCheckoutConfiguration({ uid: checkoutUid, configuration });
  };

  return (
    <Form
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema.checkout}
      renderDefaultButtons={false}
    >
      {({ values, touched, errors, setFieldTouched, setFieldValue, setValues }) => (
        <>
          {urlPreview && (
            <Form.Group>
              <Form.Label>{t('checkoutConfiguration.previewUrl')}</Form.Label>
              <p style={{ color: '#BE1622' }} className="d-block">
                {urlPreview}
              </p>
            </Form.Group>
          )}
          <FormSectionTitle>{t('checkoutConfiguration.basicConfiguration')}</FormSectionTitle>
          <Form.GroupHorizontal
            required
            label={t('checkoutConfiguration.destinationUrl')}
            tooltipText={t('checkoutConfiguration.destinationUrlTooltip')}
          >
            <Form.Input
              name="destinationUrl"
              type="text"
              placeholder={t('checkoutConfiguration.destinationUrlPlaceholder')}
              value={values.destinationUrl}
              onChange={(e) => handleValueChange('destinationUrl', e.target.value, setValues)}
            />
          </Form.GroupHorizontal>
          <Form.GroupHorizontal
            required
            label={t('checkoutConfiguration.productEncoding')}
            tooltipText={t('checkoutConfiguration.productEncodingTooltip')}
          >
            <Form.RadioGroup
              name="productEncoding"
              value={values.productEncoding}
              onChange={(e) => handleValueChange('productEncoding', e.target.value, setValues)}
            >
              <CardsContainer>
                {quantityOptions.map(({ label, value, description, example }) => (
                  <Form.RadioCard
                    key={value}
                    name="productEncoding"
                    value={value}
                    heading={label}
                    description={description}
                    onChange={(e) =>
                      handleValueChange('productEncoding', e.target.value, setValues)
                    }
                    style={{ minWidth: '0px' }}
                  >
                    <div
                      style={{
                        overflowX: 'auto',
                        backgroundColor: '#f8f9fa',
                        padding: '12px',
                        flex: 1,
                      }}
                    >
                      <p style={{ wordBreak: 'break-all', fontSize: '0.875rem', margin: 0 }}>
                        {example}
                      </p>
                    </div>
                  </Form.RadioCard>
                ))}
              </CardsContainer>
            </Form.RadioGroup>
          </Form.GroupHorizontal>
          <Form.GroupHorizontal
            required
            label={t('checkoutConfiguration.quantityParameterName')}
            tooltipText={t('checkoutConfiguration.quantityParameterNameTooltip')}
          >
            <Form.Input
              name="quantityParameterName"
              type="text"
              placeholder={t('checkoutConfiguration.quantityParameterNamePlaceholder')}
              value={values.quantityParameterName}
              onChange={(e) =>
                handleValueChange('quantityParameterName', e.target.value, setValues)
              }
            />
          </Form.GroupHorizontal>
          <FormSectionTitle>{t('checkoutConfiguration.productParameters')}</FormSectionTitle>
          <FieldArray name="productParameters">
            <div>
              {values.productParameters?.length === 0 ? (
                <div
                  style={{
                    padding: '1rem',
                    backgroundColor: '#f8f9fa',
                    border: '1px dashed #ced4da',
                    borderRadius: '8px',
                    textAlign: 'center',
                    color: '#6c757d',
                    marginBottom: '1rem',
                  }}
                >
                  {t('checkoutConfiguration.noProductParameters')}
                </div>
              ) : (
                values.productParameters?.map((parameter, index) => (
                  <TwoColumnContainer key={index}>
                    <Form.GroupHorizontal
                      required
                      label={`${t('checkoutConfiguration.productParameter')} #${index + 1}`}
                      tooltipText={t('checkoutConfiguration.productParameterTooltip')}
                      style={{ marginBottom: 8 }}
                    >
                      <Form.Select
                        name={`productParameters.${index}.productField`}
                        onChange={(e) =>
                          handleValueChange(
                            `productParameters.${index}.productField`,
                            e.value,
                            setValues,
                          )
                        }
                        options={productKeys.map((key) => ({
                          label: key,
                          value: key,
                        }))}
                      />
                    </Form.GroupHorizontal>
                    <Form.GroupHorizontal
                      required
                      label={`${t('checkoutConfiguration.productParameterName')} #${index + 1}`}
                      tooltipText={t('checkoutConfiguration.productParameterNameTooltip')}
                    >
                      <Form.Input
                        name={`productParameters.${index}.parameterName`}
                        value={parameter.parameterName}
                        placeholder={t('checkoutConfiguration.productParameterNamePlaceholder')}
                        onChange={(e) =>
                          handleValueChange(
                            `productParameters.${index}.parameterName`,
                            e.target.value,
                            setValues,
                          )
                        }
                      />
                    </Form.GroupHorizontal>
                  </TwoColumnContainer>
                ))
              )}
              {typeof errors.productParameters === 'string' && (
                <ErrorFeedback>{errors.productParameters}</ErrorFeedback>
              )}
              <Form.Group className="w-100 d-flex justify-content-end" style={{ gap: '10px' }}>
                <Button
                  className="d-flex align-items-center justify-content-center"
                  onClick={() => {
                    setFieldValue('productParameters', values.productParameters.slice(0, -1));
                  }}
                  disabled={values.productParameters.length <= 1}
                >
                  <FaMinus className="m-0 p-0" />
                </Button>
                <Button
                  className="p-3 d-flex align-items-center justify-content-center"
                  onClick={() => {
                    setFieldValue('productParameters', [
                      ...values.productParameters,
                      { productField: '', parameterName: '' },
                    ]);
                  }}
                >
                  <FaPlus className="m-0 p-0" />
                </Button>
              </Form.Group>
            </div>
          </FieldArray>
          <FormSectionTitle>{t('checkoutConfiguration.customParameters')}</FormSectionTitle>
          <FieldArray name="customParameters">
            <div>
              {values.customParameters?.length === 0 ? (
                <div
                  style={{
                    padding: '1rem',
                    backgroundColor: '#f8f9fa',
                    border: '1px dashed #ced4da',
                    borderRadius: '8px',
                    textAlign: 'center',
                    color: '#6c757d',
                    marginBottom: '1rem',
                  }}
                >
                  {t('checkoutConfiguration.noCustomParameters')}
                </div>
              ) : (
                values.customParameters?.map((field, index) => (
                  <TwoColumnContainer key={index}>
                    <Form.GroupHorizontal
                      required
                      label={`${t('checkoutConfiguration.customParameter')} #${index + 1}`}
                      tooltipText={t('checkoutConfiguration.customParameterTooltip')}
                      style={{ marginBottom: 8 }}
                    >
                      <Form.Input
                        name={`customParameters.${index}.key`}
                        value={field.key}
                        placeholder={t('checkoutConfiguration.customParameterPlaceholder')}
                        onChange={(e) =>
                          handleValueChange(
                            `customParameters.${index}.key`,
                            e.target.value,
                            setValues,
                          )
                        }
                      />
                    </Form.GroupHorizontal>
                    <Form.GroupHorizontal
                      required
                      label={`${t('checkoutConfiguration.customParameterValue')} #${index + 1}`}
                      tooltipText={t('checkoutConfiguration.customParameterValueTooltip')}
                    >
                      <Form.Input
                        name={`customParameters.${index}.value`}
                        value={field.value}
                        placeholder={t('checkoutConfiguration.customParameterValuePlaceholder')}
                        onChange={(e) =>
                          handleValueChange(
                            `customParameters.${index}.value`,
                            e.target.value,
                            setValues,
                          )
                        }
                      />
                    </Form.GroupHorizontal>
                  </TwoColumnContainer>
                ))
              )}
            </div>
          </FieldArray>
          <Form.Group className="w-100 d-flex justify-content-end" style={{ gap: '10px' }}>
            <Button
              className="d-flex align-items-center justify-content-center"
              onClick={() => {
                setFieldValue('customParameters', values.customParameters.slice(0, -1));
              }}
              disabled={values.customParameters.length === 0}
            >
              <FaMinus className="m-0 p-0" />
            </Button>
            <Button
              className="p-3 d-flex align-items-center justify-content-center"
              onClick={() => {
                setFieldValue('customParameters', [
                  ...values.customParameters,
                  { key: '', value: '' },
                ]);
              }}
            >
              <FaPlus className="m-0 p-0" />
            </Button>
          </Form.Group>
          <FormSectionTitle>{t('checkoutConfiguration.trackingAndAnalytics')}</FormSectionTitle>
          <Form.GroupVertical
            label={t('checkoutConfiguration.locationParameterName')}
            tooltipText={t('checkoutConfiguration.locationParameterNameTooltip')}
          >
            <Form.Input
              name="locationTrackingIdName"
              type="text"
              placeholder={t('checkoutConfiguration.locationParameterNamePlaceholder')}
              value={values.locationTrackingIdName}
              onChange={(e) =>
                handleValueChange('locationTrackingIdName', e.target.value, setValues)
              }
            />
          </Form.GroupVertical>
          <Form.GroupVertical
            label={t('checkoutConfiguration.publisherParameterName')}
            tooltipText={t('checkoutConfiguration.publisherParameterNameTooltip')}
          >
            <Form.Input
              name="retailerTrackingIdName"
              type="text"
              placeholder={t('checkoutConfiguration.publisherParameterNamePlaceholder')}
              value={values.retailerTrackingIdName}
              onChange={(e) =>
                handleValueChange('retailerTrackingIdName', e.target.value, setValues)
              }
            />
          </Form.GroupVertical>
          <Form.GroupVertical
            label={t('checkoutConfiguration.brandTrackingParameterName')}
            tooltipText={t('checkoutConfiguration.brandTrackingParameterNameTooltip')}
          >
            <Form.Input
              name="brandTrackingIdName"
              type="text"
              placeholder={t('checkoutConfiguration.brandTrackingParameterNamePlaceholder')}
              value={values.brandTrackingIdName}
              onChange={(e) => handleValueChange('brandTrackingIdName', e.target.value, setValues)}
            />
          </Form.GroupVertical>
          <FormSectionTitle>{t('checkoutConfiguration.redirectParameters')}</FormSectionTitle>
          <Form.GroupVertical
            label={t('checkoutConfiguration.redirectUrlParameterName')}
            tooltipText={t('checkoutConfiguration.redirectUrlParameterNameTooltip')}
          >
            <Form.Input
              name="redirectUrlParameterName"
              type="text"
              placeholder={t('checkoutConfiguration.redirectUrlParameterNamePlaceholder')}
              value={values.redirectUrlParameterName}
              onChange={(e) =>
                handleValueChange('redirectUrlParameterName', e.target.value, setValues)
              }
            />
          </Form.GroupVertical>
          <Form.GroupVertical
            label={t('checkoutConfiguration.redirectUrl')}
            tooltipText={t('checkoutConfiguration.redirectUrlTooltip')}
          >
            <Form.Input
              name="redirectUrl"
              type="text"
              placeholder={t('checkoutConfiguration.redirectUrlPlaceholder')}
              value={values.redirectUrl}
              onChange={(e) => {
                const { value } = e.target;
                handleValueChange('redirectUrl', value, setValues);
                if (value && !touched.redirectUrlParameterName) {
                  setFieldTouched('redirectUrlParameterName', true, true);
                }
              }}
            />
          </Form.GroupVertical>
          <Form.Group style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Form.Submit pristine>
              {t('checkoutConfiguration.saveButton').toUpperCase()}
            </Form.Submit>
          </Form.Group>
        </>
      )}
    </Form>
  );
};

const FormSectionTitle = styled.h2`
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 8px;
  margin-bottom: 16px;
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: 1fr;
  gap: 10px;

  ${media.lg`
    grid-template-columns: 1fr 1fr 1fr;
  `}
`;

const TwoColumnContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto auto;

  ${media.md`
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
    gap: 10px;
  `}
`;

CheckoutConfigForm.propTypes = {
  checkoutUid: PropTypes.string.isRequired,
  initialValues: PropTypes.object,
};

export default CheckoutConfigForm;
